import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Trash2, Plus, Power } from "lucide-react";
import { api } from "../lib/api";

const EMPTY = { code: "", discount_type: "percent", discount_value: "", expires_at: "", max_uses: "" };

export function PromoManager({ scope }) {
  const base = scope === "admin" ? "/admin/promos" : "/vendor/promos";
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get(base).then((r) => setPromos(r.data)).catch(console.error), [base]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.discount_value) { toast.error("Code et valeur requis"); return; }
    setSaving(true);
    try {
      await api.post(base, {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        active: true,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      });
      toast.success("Code promo créé", { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
      setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => { await api.patch(`${base}/${p.id}`, { active: !p.active }); load(); };
  const del = async (p) => { if (!window.confirm(`Supprimer le code ${p.code} ?`)) return; await api.delete(`${base}/${p.id}`); load(); };

  return (
    <div>
      <div className="border border-zinc-800 bg-ink-surface p-4 mb-6 text-sm text-zinc-400 flex items-center gap-2">
        <Tag className="h-5 w-5 text-neon-magenta" />
        {scope === "admin"
          ? "Codes promo NATIONAUX : la réduction est absorbée par le gain usine (maison mère). Valables sur toutes les boutiques."
          : "Tes codes promo : la réduction est déduite de TA marge. 🔒 Elle est automatiquement plafonnée à ta marge (jamais de vente à perte). Valables uniquement dans ta boutique."}
      </div>

      <form onSubmit={create} className="bg-ink-surface border border-zinc-800 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="promo-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE (ex: STUFF10)" className="bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
        <div className="flex gap-2">
          <select data-testid="promo-type-select" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta">
            <option value="percent">Pourcentage %</option>
            <option value="amount">Montant €</option>
          </select>
          <input data-testid="promo-value-input" type="number" step="0.01" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder={form.discount_type === "percent" ? "10" : "5.00"} className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
        </div>
        <label className="text-zinc-500 text-xs">Expiration (optionnel)
          <input data-testid="promo-expiry-input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 mt-1 focus:outline-none focus:border-neon-magenta" />
        </label>
        <label className="text-zinc-500 text-xs">Nb d'utilisations max (optionnel)
          <input data-testid="promo-maxuses-input" type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="illimité" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 mt-1 focus:outline-none focus:border-neon-magenta" />
        </label>
        <button type="submit" data-testid="promo-create-btn" disabled={saving} className="sm:col-span-2 flex items-center justify-center gap-2 bg-neon-magenta text-white font-anton uppercase py-2 glow-magenta disabled:opacity-50"><Plus className="h-4 w-4" /> Créer le code</button>
      </form>

      {promos.length === 0 ? <p className="text-zinc-500 py-6 text-center">Aucun code promo pour l'instant.</p> : (
        <div className="space-y-2">
          {promos.map((p) => (
            <div key={p.id} data-testid="promo-row" className="bg-ink-surface border border-zinc-800 p-3 flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="font-anton text-lg text-neon-magenta tracking-wider">{p.code}</span>
                <span className="ml-3 text-sm text-zinc-300">{p.discount_type === "percent" ? `-${p.discount_value}%` : `-${Number(p.discount_value).toFixed(2)}€`}</span>
                <span className="ml-3 text-xs text-zinc-500">{p.used_count} util.{p.max_uses ? ` / ${p.max_uses}` : ""}{p.expires_at ? ` · exp. ${new Date(p.expires_at).toLocaleDateString("fr-FR")}` : ""}</span>
                {scope === "admin" && <span className={`ml-3 text-[10px] uppercase font-bold px-1.5 py-0.5 border ${p.owner_type === "admin" ? "text-neon-green border-neon-green/50" : "text-zinc-400 border-zinc-600"}`}>{p.owner_type === "admin" ? "National" : "Vendeur"}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="promo-toggle" onClick={() => toggle(p)} title={p.active ? "Désactiver" : "Activer"} className={`p-2 border ${p.active ? "border-neon-green text-neon-green" : "border-zinc-700 text-zinc-500"}`}><Power className="h-4 w-4" /></button>
                <button data-testid="promo-delete" onClick={() => del(p)} className="p-2 text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
